import React, { useMemo, useEffect } from "react";
import { FaRegCopy } from "react-icons/fa";
import styles from "./style.module.scss";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../state";
import { isEqual } from "lodash";
import {
  setSelectedSeason,
  setSelectedEvent,
  setSelectedSession,
  toggleSelectedSessionIsReal,
} from "../../../state/replayReducer";
import { AvailableSession } from "../../../types/replay";
import {
  QueryProcessedSessionDataAndV2Merged,
  QueryReplayEventsForSeason,
  QueryReplaySessionsForEvent,
  SetReplaySessionIsReal,
} from "../../../utils/graphql/hooks/replayHooks";

export default function ReplaySelector() {
  const dispatch = useDispatch();
  const fetchSessionEventData = QueryProcessedSessionDataAndV2Merged();
  const fetchEventsForSeason = QueryReplayEventsForSeason();
  const fetchSessionsForEvent = QueryReplaySessionsForEvent();
  const setReplaySessionIsReal = SetReplaySessionIsReal();

  const availableSeasons = useSelector(
    (state: RootState) => state.replay.availableSeasons,
    isEqual,
  );

  const availableEvents = useSelector(
    (state: RootState) => state.replay.availableEvents,
    isEqual,
  );

  const availableSessions = useSelector(
    (state: RootState) => state.replay.availableSessions,
  );

  const loadingSeasons = useSelector(
    (state: RootState) => state.replay.loadingSeasons,
  );

  const loadingEvents = useSelector(
    (state: RootState) => state.replay.loadingEvents,
  );

  const loadingSessions = useSelector(
    (state: RootState) => state.replay.loadingSessions,
  );

  const selectedSeason = useSelector(
    (state: RootState) => state.replay.selectedSeason,
    isEqual,
  );

  const selectedEvent = useSelector(
    (state: RootState) => state.replay.selectedEvent,
    isEqual,
  );

  const selectedSession = useSelector(
    (state: RootState) => state.replay.selectedSession,
  );

  useEffect(() => {
    if (selectedSeason !== null) {
      fetchEventsForSeason(selectedSeason);
    }
  }, [selectedSeason]);

  useEffect(() => {
    if (selectedSeason !== null && selectedEvent !== null) {
      fetchSessionsForEvent(selectedSeason, selectedEvent);
    }
  }, [selectedSeason, selectedEvent]);

  const events = useMemo(() => {
    return [...availableEvents].sort((a, b) => b.latestDt - a.latestDt);
  }, [availableEvents]);

  const sessions = useMemo(() => {
    return [...availableSessions].sort((a, b) => b.dt - a.dt);
  }, [availableSessions]);

  const handleSeasonChange = (season: number) => {
    dispatch(setSelectedSeason(season));
  };

  const handleEventChange = (event: string) => {
    dispatch(setSelectedEvent(event));
  };

  const handleSessionClick = (session: AvailableSession) => {
    dispatch(setSelectedSession(session));
    fetchSessionEventData(session.sessionId);
  };

  const handleCopySessionId = () => {
    if (selectedSession?.sessionId) {
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(selectedSession.sessionId);
      } else {
        // Fallback for trackside (HTTP) version (depreciated function)
        const textArea = document.createElement("textarea");
        textArea.value = selectedSession.sessionId;
        textArea.style.position = "fixed";
        textArea.style.opacity = "0";
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
      }
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.selectorContainer}>
        <div className={styles.header}>
          <div className={styles.filters}>
            <select
              className={styles.selector}
              style={{ width: "90px" }}
              onChange={(e) => handleSeasonChange(Number(e.target.value))}
              value={selectedSeason ?? ""}
              disabled={loadingSeasons || availableSeasons.length === 0}
            >
              <option value="" disabled hidden>
                Season
              </option>
              {availableSeasons.map((season) => (
                <option key={season} value={season}>
                  Season {season}
                </option>
              ))}
            </select>
            <select
              className={styles.selector}
              style={{ flex: 1, minWidth: 0 }}
              onChange={(e) => handleEventChange(e.target.value)}
              value={selectedEvent ?? ""}
              disabled={loadingEvents || !selectedSeason || events.length === 0}
            >
              <option value="" disabled hidden>
                Event
              </option>
              {events.map((event) => (
                <option key={event.eventName} value={event.eventName}>
                  {event.eventName}
                </option>
              ))}
            </select>
          </div>
        </div>
        {loadingSeasons ? (
          <div className={styles.loadingContainer}>
            <div className={styles.spinner} />
          </div>
        ) : (
          <div className={styles.tableContainer}>
            <table className={styles.sessionsTable}>
              <tbody>
                {loadingSessions ? (
                  <tr>
                    <td colSpan={2} className={styles.loadingCell}>
                      <div className={styles.spinner} />
                    </td>
                  </tr>
                ) : sessions.length > 0 ? (
                  sessions.map((session) => {
                    const isSelected =
                      selectedSession?.sessionId === session.sessionId;
                    return (
                      <tr
                        key={session.sessionId}
                        className={isSelected ? styles.selectedRow : ""}
                        onClick={() => handleSessionClick(session)}
                      >
                        <td>
                          {session.sessionName}
                          {session.isReal ? " ✓" : ""}
                        </td>
                        <td>{new Date(session.dt).toLocaleString()}</td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={2} className={styles.logEntry}>
                      {selectedEvent
                        ? "No sessions available"
                        : "Select a season and event"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <div className={styles.editContainer}>
        <label className={styles.radioOption}>
          <input
            type="checkbox"
            checked={selectedSession?.isReal ?? false}
            onChange={async (e) => {
              const result = await setReplaySessionIsReal({
                variables: {
                  sessionId: selectedSession?.sessionId ?? "",
                  isReal: e.target.checked,
                },
              });
              if (result.data?.setReplaySessionIsReal) {
                dispatch(toggleSelectedSessionIsReal());
              }
            }}
          />
          Real Session
        </label>
        {selectedSession?.sessionId && (
          <div className={styles.sessionIdContainer}>
            <span className={styles.sessionId}>
              {selectedSession.sessionId}
            </span>
            <button
              type="button"
              className={styles.copyButton}
              onClick={handleCopySessionId}
            >
              <FaRegCopy />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
